// Shared pricing plans configuration
export interface PricingPlan {
  name: string;
  badge: string;
  tier: string;
  priceMonthly: string;
  priceQuarterly: string;
  priceYearly: string;
  integration: string;
  total?: string;
  totalYearly?: string;
  features: string[];
  integrationServices: Array<{
    name: string;
    cost: string;
  }>;
  popular?: boolean;
  gradient?: string;
  isVip?: boolean;
  button?: string;
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    name: "Starter Plan",
    badge: "For Growing HR Teams",
    tier: "STARTER",
    priceMonthly: "₦100,000",
    priceQuarterly: "₦300,000",
    priceYearly: "₦1,200,000",
    integration: "₦200,000",
    total: "300,000",
    totalYearly: "7,900,000",
    features: [
      "Up to 50 AI-powered interviews/month",
      "Up to 3 Admin usage",
      "AI-driven job creation system",
      "Basic reporting & grading",
      "Pre-test assessments",
      "15GB video storage (3 months)",
      "Priority support",
    ],
    integrationServices: [
      { name: "Standard Onboarding & Training", cost: "₦1,400,000" },
    ],
    popular: false,
    gradient: "from-[#2196F3] to-[#1E88E5]",
    button: "Get Now",
  },
  {
    name: "Growth Plan",
    badge: "Most Popular",
    tier: "GROWTH",
    priceMonthly: "₦300,000",
    priceQuarterly: "₦900,000",
    priceYearly: "₦3,600,000",
    integration: "₦500,000",
    total: "800,000",
    totalYearly: "4,100,000",
    features: [
      "Up to 100 AI-powered interviews/month",
      "Up to 5 Admin usage",
      "Comprehensive reporting & analytics",
      "Advanced interview dashboard",
      "30GB video storage (6 months)",
      "Dedicated account manager",
      "Custom integrations & API access",
    ],
    integrationServices: [
      { name: "Standard Onboarding & Training", cost: "₦800,000" },
      { name: "Data Migration & Import", cost: "₦1,200,000" },
      { name: "Quality Assurance & Launch Support", cost: "₦800,000" },
      { name: "Total Integration", cost: "₦2,800,000" },
    ],
    popular: true,
    gradient: "from-[#1E88E5] to-[#1976D2]",
    button: "Get Plan",
  },
  {
    name: "Scale Plan",
    badge: "Advanced Integrations",
    tier: "SCALE",
    priceMonthly: "₦1,400,000",
    priceQuarterly: "₦4,200,000",
    priceYearly: "₦16,800,000",
    integration: "₦5,900,000",
    total: "7,300,000",
    totalYearly: "22,700,000",
    features: [
      "Up to 200 AI-powered interviews/month",
      "Up to 10 Admin usage",
      "Full white-label customization",
      "Custom branding and domain setup",
      "99.5% uptime SLA",
      "100GB video storage (12 months)",
      "Quarterly business reviews",
    ],
    integrationServices: [
      { name: "Standard Onboarding & Training", cost: "₦800,000" },
      { name: "Data Migration & Import", cost: "₦1,200,000" },
      { name: "Quality Assurance & Launch Support", cost: "₦800,000" },
      { name: "Platform Customization & Branding", cost: "₦1,500,000" },
      { name: "AI Configuration & Model Tuning", cost: "₦800,000" },
      { name: "System Integration & API Setup", cost: "₦800,000" },
      { name: "Total Integration", cost: "₦5,900,000" },
    ],
    popular: false,
    gradient: "from-[#1976D2] to-[#0B60D6]",
    button: "Get Plus",
  },
  {
    name: "Enterprise Plan",
    badge: "Custom Enterprise Solution",
    tier: "ENTERPRISE",
    priceMonthly: "Contact Us",
    priceQuarterly: "Contact Us",
    priceYearly: "Contact Us",
    integration: "Custom",
    features: [
      "Everything in Enterprise Plus",
      "Bespoke integrations",
      "Advanced SLA (99.9% uptime)",
      "Dedicated enterprise support",
    ],
    integrationServices: [
      { name: "Custom Integration Services", cost: "Custom Pricing" },
    ],
    popular: false,
    gradient: "from-[#1E88E5] to-[#1976D2]",
    isVip: true,
    button: "Contact Sales",
  },
];

// Helper function to get plan by tier
export const getPlanByTier = (tier: string): PricingPlan | undefined => {
  return PRICING_PLANS.find((plan) => plan.tier === tier);
};

// Helper function to get tier hierarchy index
export const getTierIndex = (tier: string): number => {
  const tiers = ["STARTER", "GROWTH", "SCALE", "ENTERPRISE"];
  return tiers.indexOf(tier);
};

// Helper function to check if upgrade is available
export const canUpgradeTo = (
  currentTier: string,
  targetTier: string,
): boolean => {
  const currentIndex = getTierIndex(currentTier);
  const targetIndex = getTierIndex(targetTier);
  return (
    targetIndex > currentIndex && currentIndex !== -1 && targetIndex !== -1
  );
};
